import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Tag,
  Space,
  Divider,
  Progress,
  Alert,
  Empty,
  Spin,
  Badge,
  Tooltip,
  Button,
  List,
  Collapse,
  Skeleton,
  Table,
} from 'antd';
import {
  BulbOutlined,
  AimOutlined,
  RiseOutlined,
  StarOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  TrophyOutlined,
  FireOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  StarFilled,
  StarTwoTone,
  CheckCircleTwoTone,
} from '@ant-design/icons';
import { message } from 'antd';
import { getYellowShade } from '../common/heatmapColors';
import TopicSunburstMap from './TopicSunburstMap';
import favoriteKeywordsApi from '../../../api/favoriteKeywords';
import './AIOContentStrategy.css';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * マークダウンの太字（**text**）をHTMLに変換するヘルパー関数
 * @param {string} text - 変換するテキスト
 * @returns {string} - HTMLに変換されたテキスト
 */
const formatBoldMarkdown = (text) => {
  if (!text || typeof text !== 'string') return '';
  // **text** を <strong>text</strong> に変換
  // XSS対策: <>をエスケープしてから太字変換
  let escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
};

const AIOContentStrategy = ({ analysisData, loading = false }) => {
  const [strategyData, setStrategyData] = useState(null);
  const [favoriteMap, setFavoriteMap] = useState({});
  const analysisId = analysisData?.analysis_id || analysisData?.analysisId;
  const mainMetrics =
    (analysisData?.keywords_analysis &&
      analysisData?.keywords_analysis?.main_keyword_metrics) ||
    {};

  // マウント/アンマウントログ
  useEffect(() => {
    (async () => {
      try {
        const resp = await favoriteKeywordsApi.getFavoriteKeywords({
          limit: 1000,
        });
        const list = Array.isArray(resp?.keywords)
          ? resp.keywords
          : Array.isArray(resp)
            ? resp
            : [];
        const map = {};
        list.forEach(item => {
          const kw = typeof item === 'string' ? item : item?.keyword;
          if (kw) map[kw] = true;
        });
        setFavoriteMap(map);
      } catch (e) {
        console.warn('[FavoriteKeywords] initial load failed:', e);
      }
    })();
    console.log('[LANDSCAPE] AIOContentStrategy mounted');
    return () => console.log('[LANDSCAPE] AIOContentStrategy unmounted');
  }, []);

  useEffect(() => {
    console.log('🔍 [AIOContentStrategy] analysisData:', analysisData);
    console.log(
      '🔍 [AIOContentStrategy] analysisData?.aio_content_strategy:',
      analysisData?.aio_content_strategy,
    );
    console.log('🔍 [AIOContentStrategy] loading:', loading);
    try {
      const tlRaw = analysisData?.topic_landscape;
      const tlAlt = analysisData?.topic_analysis?.landscape;
      const tl = tlRaw || tlAlt;
      console.log('[LANDSCAPE] useEffect probe:', {
        hasTopKey: !!tlRaw,
        hasAltKey: !!tlAlt,
        tlExists: !!tl,
      });
      if (tl) {
        let s = tl.summary;
        if (!s && typeof tl.response === 'string') {
          try {
            let txt = tl.response.trim();
            if (txt.startsWith('```json')) txt = txt.slice(7);
            if (txt.endsWith('```')) txt = txt.slice(0, -3);
            const inner = JSON.parse(txt.trim());
            s = inner?.summary;
          } catch (parseErr) {
            console.warn(
              '[LANDSCAPE] parse summary from response failed:',
              parseErr,
            );
          }
        }
        console.log('[LANDSCAPE] useEffect summary presence:', {
          hasSummary: !!s,
          summaryLen: s ? String(s).length : 0,
        });
      }
    } catch (e) {
      console.warn('[LANDSCAPE] useEffect probe failed:', e);
    }

    if (analysisData?.aio_content_strategy) {
      let raw = analysisData.aio_content_strategy;
      let parsed = null;
      try {
        if (typeof raw === 'string') {
          let txt = raw.trim();
          if (txt.startsWith('```json')) txt = txt.slice(7);
          if (txt.endsWith('```')) txt = txt.slice(0, -3);
          parsed = JSON.parse(txt);
        } else if (
          typeof raw === 'object' &&
          raw !== null &&
          !raw.ai_blocks &&
          typeof raw.response === 'string'
        ) {
          let txt = raw.response.trim();
          if (txt.startsWith('```json')) txt = txt.slice(7);
          if (txt.endsWith('```')) txt = txt.slice(0, -3);
          parsed = JSON.parse(txt);
        }
      } catch (e) {
        console.warn(
          '[AIOContentStrategy] parse strategy from response failed:',
          e,
        );
      }
      let strategy = parsed || raw;
      // keyword フォールバック（保存データに空文字が入っている既存分の対策）
      try {
        if (
          strategy &&
          typeof strategy === 'object' &&
          (!strategy.keyword || String(strategy.keyword).trim() === '') &&
          analysisData?.keyword
        ) {
          strategy = { ...strategy, keyword: analysisData.keyword };
        }
      } catch (e) {
        /* noop: keyword補完の失敗は無視 */
      }

      // ユーザー要望により合成アダプターは無効化

      setStrategyData(strategy);
      console.log('✅ [AIOContentStrategy] Strategy data set:', strategy);
    } else {
      console.log('❌ [AIOContentStrategy] No aio_content_strategy found');
    }
  }, [analysisData, loading]);

  const toggleFavorite = async keyword => {
    if (!keyword) return;
    const prev = !!favoriteMap[keyword];
    setFavoriteMap(m => ({ ...m, [keyword]: !prev }));
    try {
      if (!prev) {
        await favoriteKeywordsApi.addFavoriteKeyword({
          keyword,
          searchVolume: mainMetrics?.search_volume,
          keywordDifficulty: mainMetrics?.keyword_difficulty,
          cpc: mainMetrics?.cpc,
          sourceAnalysisId: analysisId,
        });
      } else {
        await favoriteKeywordsApi.deleteFavoriteKeyword(keyword);
      }
    } catch (e) {
      // revert on error
      setFavoriteMap(m => ({ ...m, [keyword]: prev }));
      console.warn('[FavoriteKeywords] toggle failed:', e);
    }
  };

  const ensureFavorite = async keyword => {
    if (!keyword) return;
    if (favoriteMap[keyword]) return;
    setFavoriteMap(m => ({ ...m, [keyword]: true }));
    try {
      await favoriteKeywordsApi.addFavoriteKeyword({
        keyword,
        searchVolume: mainMetrics?.search_volume,
        keywordDifficulty: mainMetrics?.keyword_difficulty,
        cpc: mainMetrics?.cpc,
        sourceAnalysisId: analysisId,
      });
      message.success(`「${keyword}」をお気に入りに追加しました`);
    } catch (e) {
      setFavoriteMap(m => ({ ...m, [keyword]: false }));
      console.warn('[FavoriteKeywords] ensure failed:', e);
    }
  };

  if (loading || strategyData === null) {
    return (
      <div style={{ padding: '24px' }}>
        {/* 概要セクションのスケルトン */}
        <Card style={{ marginBottom: '24px' }}>
          <Skeleton
            active
            paragraph={{ rows: 8 }}
            title={{ width: '30%' }}
            className="pulse"
          />
        </Card>

        {/* トピック候補セクションのスケルトン */}
        <Card style={{ marginBottom: '24px' }}>
          <Skeleton
            active
            paragraph={{ rows: 2 }}
            title={{ width: '40%' }}
            className="pulse"
          />
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Col xs={24} md={12} key={i}>
                <Card size="small">
                  <Skeleton
                    active
                    paragraph={{ rows: 3 }}
                    title={{ width: '60%' }}
                    className="pulse"
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        {/* さらなる高みセクションのスケルトン */}
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map(i => (
            <Col xs={24} md={8} key={i}>
              <Card>
                <Skeleton
                  active
                  paragraph={{ rows: 4 }}
                  title={{ width: '70%' }}
                  className="pulse"
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  // strategyData が無くても、ランドスケープ等は analysisData から描画できるため
  // 早期リターンせずに各カード側でEmptyを表示する

  // エラー情報（存在しても描画は継続し、上部に警告を表示）
  const hasError = !!strategyData?.error_info?.error_occurred;
  const errorMsg = strategyData?.error_info?.error_message;

  const { ai_strategy, content_sections, enhancement_suggestions } =
    strategyData || {};

  console.log('🎯 [AIOContentStrategy] About to render main content:', {
    hasAiStrategy: !!ai_strategy,
    hasContentSections: !!content_sections,
    hasEnhancementSuggestions: !!enhancement_suggestions,
  });

  // トピックカードのレンダリング（2カラム用）
  const renderTopicCard = (section, label, labelColor) => {
    // ラベルに応じたアイコンを選択
    const getIconForLabel = label => {
      switch (label) {
        case '必須':
          return <FireOutlined style={{ color: 'white', fontSize: '16px' }} />;
        case '推奨':
          return <RiseOutlined style={{ color: 'white', fontSize: '16px' }} />;
        case '独自':
          return (
            <TrophyOutlined style={{ color: 'white', fontSize: '16px' }} />
          );
        default:
          return <StarOutlined style={{ color: 'white', fontSize: '16px' }} />;
      }
    };

    return (
      <Card
        size="small"
        className="topic-card"
        style={{
          backgroundColor: '#ffffff',
          border: '3px solid #f0f0f0',
          paddingLeft: '60px', // 左側のアイコンエリア用のスペース
          position: 'relative',
          marginBottom: '12px',
        }}
      >
        {/* 左側のアイコン＋ラベル */}
        <div
          style={{
            position: 'absolute',
            left: '24px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {/* 丸背景アイコン */}
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: labelColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getIconForLabel(label)}
          </div>
          {/* ラベルテキスト */}
          <Text
            style={{
              fontSize: '13px',
              fontWeight: 'bold',
              color: labelColor,
              textAlign: 'center',
            }}
          >
            {label}
          </Text>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <Text strong style={{ fontSize: '16px' }}>
            {section.section_title}
          </Text>
        </div>

        <Paragraph style={{ fontSize: '14px', color: '#666', margin: '8px 0' }}>
          {section.section_description}
        </Paragraph>

        {section.content_requirements && (
          <div
            style={{
              marginTop: '12px',
              padding: '8px',
              backgroundColor: '#fafafa',
              borderRadius: '4px',
            }}
          >
            <Text style={{ fontSize: '14px', color: '#666' }}>
              <strong>見出し例:</strong>{' '}
              {section.content_requirements.optimal_heading}
            </Text>
          </div>
        )}

        {section.supporting_keywords &&
          section.supporting_keywords.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ marginBottom: '4px' }}>
                <Text strong style={{ fontSize: '13px', color: '#000' }}>
                  サブクエリのカバー:
                </Text>
              </div>
              <Space wrap>
                {section.supporting_keywords.map((keyword, index) => (
                  <Tag
                    key={index}
                    size="small"
                    style={{
                      backgroundColor: '#f0f0f0',
                      color: '#000',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {keyword}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
      </Card>
    );
  };

  // セクションカードのレンダリング（改善版）
  const renderSectionCard = (section, categoryColor) => (
    <Card
      key={section.section_id}
      size="small"
      style={{
        marginBottom: '12px',
        backgroundColor: '#f8f8f8',
        border: 'none',
      }}
      hoverable
    >
      <div style={{ marginBottom: '8px' }}>
        <Text strong style={{ fontSize: '16px' }}>
          {section.section_title}
        </Text>
      </div>

      <Paragraph style={{ fontSize: '13px', color: '#666', margin: '8px 0' }}>
        {section.section_description}
      </Paragraph>

      {section.content_requirements && (
        <div
          style={{
            marginTop: '12px',
            padding: '8px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
          }}
        >
          <Text style={{ fontSize: '13px', color: '#666' }}>
            <strong>見出し例:</strong>{' '}
            {section.content_requirements.optimal_heading}
          </Text>
        </div>
      )}

      {section.supporting_keywords &&
        section.supporting_keywords.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <Text strong style={{ fontSize: '13px', color: '#000' }}>
                サブクエリのカバー:
              </Text>
            </div>
            <Space wrap>
              {section.supporting_keywords.map((keyword, index) => (
                <Tag key={index} size="small" color="blue">
                  {keyword}
                </Tag>
              ))}
            </Space>
          </div>
        )}
    </Card>
  );

  return (
    <div
      className="fade-in aio-content-strategy"
      style={{
        padding: '20px',
        animation: 'fadeIn 0.6s ease-in-out',
      }}
    >
      {/* 生成エラーがあれば上部に注意を表示（描画は続行） */}
      {hasError && (
        <Alert
          message="コンテンツ戦略生成エラー"
          description={`エラーが発生しました: ${errorMsg}`}
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* トピック・ランドスケープ（王道＋差別化の助言） */}
      <div className="ai-strategy-card tour-strategy-basic">
        <div>
          <Text strong className="ai-strategy-title">
            基本戦略・トピック選定方針
          </Text>
        </div>
        <Text className="ai-strategy-text">
          {(() => {
            const tl =
              analysisData?.topic_landscape ||
              analysisData?.topic_analysis?.landscape;
            let summary = tl?.summary;
            if (!summary && tl && typeof tl.response === 'string') {
              try {
                let txt = tl.response.trim();
                if (txt.startsWith('```json')) txt = txt.slice(7);
                if (txt.endsWith('```')) txt = txt.slice(0, -3);
                const inner = JSON.parse(txt.trim());
                summary = inner?.summary;
              } catch (e) {
                console.warn('[LANDSCAPE] render parse failed:', e);
              }
            }
            console.log('[LANDSCAPE] render check:', {
              tlExists: !!tl,
              hasSummary: !!summary,
              summaryLen: summary ? String(summary).length : 0,
              hasTopKey: !!analysisData?.topic_landscape,
              hasAltKey: !!analysisData?.topic_analysis?.landscape,
            });
            // tl（オブジェクト）が存在するだけでもスケルトン解除
            if (tl) {
              return (
                <div 
                  style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{
                    __html: formatBoldMarkdown(summary || ''),
                  }}
                />
              );
            }
            return (
              <Skeleton
                active
                paragraph={{ rows: 6 }}
                title={false}
                className="pulse"
              />
            );
          })()}
        </Text>
      </div>

      {/* 新レイアウト */}
      {/* AI Overviewsパーツ例 */}
      <Card
        title={<span>AI Overviewsパーツ例</span>}
        style={{ marginBottom: '24px' }}
        className="v2-card"
      >
        <Row gutter={[16, 16]}>
          {(strategyData?.ai_blocks || []).map((blk, i) => (
            <Col xs={24} md={12} key={i}>
              <Collapse size="small" accordion className="aio-blocks-collapse">
                <Panel
                  header={
                    <div className="aio-block-toggle-header">
                      {blk?.title || `パーツ ${i + 1}`}
                    </div>
                  }
                  key="1"
                  className="aio-block-panel"
                >
                  {blk?.why && (
                    <div className="reason-box">
                      <div className="reason-title">推奨する理由</div>
                      <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {blk.why}
                      </Paragraph>
                    </div>
                  )}

                  {(() => {
                    const type = (blk?.block_type || '')
                      .toString()
                      .toLowerCase();
                    // sentence
                    if (type === 'sentence' && blk?.content) {
                      return (
                        <div>
                          <Text strong>パーツの具体例</Text>
                          <Paragraph
                            style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}
                          >
                            {blk.content}
                          </Paragraph>
                        </div>
                      );
                    }
                    // list
                    if (type === 'list' && Array.isArray(blk?.bullets)) {
                      return (
                        <div>
                          <Text strong>パーツの具体例</Text>
                          <ul className="dot-list" style={{ marginTop: 8 }}>
                            {blk.bullets.map((b, idx) => (
                              <li key={idx}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    // table
                    if (type === 'table' && blk?.table) {
                      const headers = Array.isArray(blk.table.headers)
                        ? blk.table.headers
                        : [];
                      const rows = Array.isArray(blk.table.rows)
                        ? blk.table.rows
                        : [];
                      const columns = headers.map((h, idx) => ({
                        title: h,
                        dataIndex: `col_${idx}`,
                        key: `col_${idx}`,
                      }));
                      const data = rows.map((r, ri) => {
                        const obj = { key: `row_${ri}` };
                        headers.forEach((_, ci) => {
                          obj[`col_${ci}`] = Array.isArray(r) ? r[ci] : '';
                        });
                        return obj;
                      });
                      return (
                        <div style={{ marginTop: 8 }}>
                          <Text strong>パーツの具体例</Text>
                          <div style={{ marginTop: 8 }}>
                            <Table
                              size="small"
                              pagination={false}
                              columns={columns}
                              dataSource={data}
                            />
                          </div>
                        </div>
                      );
                    }
                    // fallback（旧フォーマット）
                    if (blk?.snippet) {
                      return (
                        <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                          {blk.snippet}
                        </Paragraph>
                      );
                    }
                    if (Array.isArray(blk?.bullets) && blk.bullets.length > 0) {
                      return (
                        <div>
                          <Text strong>パーツの具体例</Text>
                          <ul className="dot-list" style={{ marginTop: 8 }}>
                            {blk.bullets.map((b, idx) => (
                              <li key={idx}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </Panel>
              </Collapse>
            </Col>
          ))}
          {(!strategyData?.ai_blocks ||
            strategyData.ai_blocks.length === 0) && (
            <Col span={24}>
              <Empty description="AI Overviewsパーツがありません" />
            </Col>
          )}
        </Row>
      </Card>

      {/* E-E-A-T対策 */}
      <Card title={<span>E-E-A-T対策</span>} style={{ marginBottom: '24px' }} className="tour-strategy-eeat v2-card">
        <Collapse
          bordered={false}
          defaultActiveKey={Object.keys(strategyData?.eeat || {})}
          className="aio-blocks-collapse"
        >
          {Object.entries(strategyData?.eeat || {})
            .sort(([a], [b]) => {
              const order = {
                experience: 0,
                expertise: 1,
                authoritativeness: 2,
                trust: 3,
              };
              const ka = String(a || '').toLowerCase();
              const kb = String(b || '').toLowerCase();
              const oa =
                order[ka] ??
                (ka.startsWith('e') ? (ka.includes('exper') ? 1 : 0) : 99);
              const ob =
                order[kb] ??
                (kb.startsWith('e') ? (kb.includes('exper') ? 1 : 0) : 99);
              return oa - ob;
            })
            .map(([cat, payload], idx) => (
              <Panel
                header={cat.toUpperCase()}
                key={cat}
                className="aio-block-panel"
              >
                {(() => {
                  const items = [];
                  if (Array.isArray(payload?.must)) {
                    payload.must.forEach(t =>
                      items.push({ type: 'must', text: t }),
                    );
                  }
                  if (Array.isArray(payload?.recommended)) {
                    payload.recommended.forEach(t =>
                      items.push({ type: 'recommended', text: t }),
                    );
                  }
                  if (items.length === 0) {
                    return <Empty description="項目がありません" />;
                  }
                  return (
                    <List
                      size="small"
                      dataSource={items}
                      renderItem={(it, i2) => (
                        <List.Item key={i2} style={{ padding: '6px 0' }}>
                          <Space>
                            <Tag
                              color={it.type === 'must' ? 'gold' : 'geekblue'}
                            >
                              {it.type === 'must' ? '重要' : '推奨'}
                            </Tag>
                            <span>{it.text}</span>
                          </Space>
                        </List.Item>
                      )}
                    />
                  );
                })()}
              </Panel>
            ))}
          {(!strategyData?.eeat ||
            Object.keys(strategyData.eeat).length === 0) && (
            <Panel header="E-E-A-T" key="empty">
              <Empty description="E-E-A-T対策がありません" />
            </Panel>
          )}
        </Collapse>
      </Card>

      {/* トピックとの関連性が高いキーワード案 */}
      <Card
        className="v2-card"
        title={<span>トピックとの関連性が高いキーワード案</span>}
        hoverable={false}
        bodyStyle={{ padding: '12px' }}
        style={{ marginBottom: '24px' }}
      >
        <Space wrap size="small">
          {(strategyData?.subqueries?.covered_by_this_article || []).map(
            (q, qi) => (
              <Tag
                key={qi}
                className={`fav-tag`}
                onClick={() => {
                  toggleFavorite(q);
                  if (!favoriteMap[q]) {
                    try {
                      message.success(`「${q}」をお気に入りに追加しました`);
                    } catch (e) {
                      console.warn(
                        '[FavoriteKeywords] success message failed:',
                        e,
                      );
                    }
                  } else {
                    try {
                      message.success(`「${q}」をお気に入りから削除しました`);
                    } catch (e) {
                      console.warn(
                        '[FavoriteKeywords] success message failed:',
                        e,
                      );
                    }
                  }
                }}
                style={{ padding: '4px 12px', cursor: 'pointer' }}
              >
                {favoriteMap[q] && (
                  <span style={{ marginRight: 6 }}>
                    <StarFilled style={{ color: '#faad14' }} />
                  </span>
                )}
                {q}
              </Tag>
            ),
          )}
          {(!strategyData?.subqueries?.covered_by_this_article ||
            strategyData.subqueries.covered_by_this_article.length === 0) && (
            <Empty description="候補がありません" />
          )}
        </Space>
      </Card>

      {/* 別の記事で補強するアイデア */}
      <Card
        title={<span>別の記事で補強するアイデア</span>}
        style={{ marginBottom: '24px' }}
        className="tour-strategy-subqueries v2-card"
      >
        <Table
          size="small"
          pagination={false}
          dataSource={(strategyData?.subqueries?.separate_articles || []).map(
            (it, idx) => ({
              key: idx,
              title: it.title,
              reason: it.why,
              targets: it.target_queries || [],
            }),
          )}
          columns={[
            {
              title: 'タイトル案',
              dataIndex: 'title',
              key: 'title',
              render: v => v || '-',
            },
            {
              title: '別記事にする理由',
              dataIndex: 'reason',
              key: 'reason',
              render: v =>
                v ? <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span> : '-',
            },
            {
              title: 'ターゲットキーワード例',
              dataIndex: 'targets',
              key: 'targets',
              render: arr => (
                <div style={{ maxWidth: '40%' }}>
                  <Space wrap size="small">
                    {(arr || []).map((qq, i) => (
                      <Tag
                        key={i}
                        onClick={() => {
                          toggleFavorite(qq);
                          if (!favoriteMap[qq]) {
                            try {
                              message.success(
                                `「${qq}」をお気に入りに追加しました`,
                              );
                            } catch (e) {
                              console.warn(
                                '[FavoriteKeywords] success message failed:',
                                e,
                              );
                            }
                          } else {
                            try {
                              message.success(
                                `「${qq}」をお気に入りから削除しました`,
                              );
                            } catch (e) {
                              console.warn(
                                '[FavoriteKeywords] success message failed:',
                                e,
                              );
                            }
                          }
                        }}
                        style={{ padding: '4px 12px', cursor: 'pointer' }}
                      >
                        {favoriteMap[qq] && (
                          <span style={{ marginRight: 6 }}>
                            <StarFilled style={{ color: '#faad14' }} />
                          </span>
                        )}
                        {qq}
                      </Tag>
                    ))}
                  </Space>
                </div>
              ),
              width: '35%',
            },
          ]}
          locale={{ emptyText: <Empty description="候補がありません" /> }}
        />
      </Card>

      {/* 読者満足度を最大化するために */}
      <Card
        title={<span>読者満足度を最大化するために</span>}
        className="reader-satisfaction-card tour-strategy-uniqueness v2-card"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="ai-strategy-card" style={{ height: '100%' }}>
              <div>
                <Text strong className="ai-strategy-title">
                  コア設計
                </Text>
              </div>
              <Text className="ai-strategy-text">
                <Paragraph
                  style={{
                    marginTop: 8,
                    whiteSpace: 'pre-wrap',
                    marginBottom: 0,
                  }}
                >
                  {strategyData?.uvp || ''}
                </Paragraph>
              </Text>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Card
              size="small"
              className="article-subcard"
              style={{ height: '100%' }}
            >
              <Text strong>独自性を高めるアイデア</Text>
              <div style={{ marginTop: 8 }}>
                {(strategyData?.uniqueness_ideas || []).map((u, ui) => (
                  <div
                    key={ui}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      margin: '6px 0',
                    }}
                  >
                    <CheckCircleTwoTone
                      twoToneColor="#52c41a"
                      style={{ marginRight: 8 }}
                    />
                    <span>{u}</span>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default AIOContentStrategy;
